import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { useHomepageSection, useHomepageSections } from "../../store/adminDataStore";

const VALUES = [
  { titleKey: "tabifoto.philosophy.value_1_title", bodyKey: "tabifoto.philosophy.value_1_body", icon: "ri-heart-line" },
  { titleKey: "tabifoto.philosophy.value_2_title", bodyKey: "tabifoto.philosophy.value_2_body", icon: "ri-user-line" },
  { titleKey: "tabifoto.philosophy.value_3_title", bodyKey: "tabifoto.philosophy.value_3_body", icon: "ri-leaf-line" },
];

export default function PhilosophyPage() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language?.split("-")[0] ?? "en") as "zh" | "en" | "ja";

  // Load from homepage_sections
  const philHero = useHomepageSection("philosophy"); // sort_order = 0, the hero block
  const philSections = useHomepageSections("philosophy").filter((s) => s.sort_order > 0);
  const ctaSection = useHomepageSection("cta");

  const heroTitle = philHero?.[`title_${lang}`] || philHero?.title_en || t("tabifoto.philosophy.hero_title");
  const heroSub = philHero?.[`content_${lang}`] || philHero?.content_en || t("tabifoto.philosophy.hero_sub");
  const heroImage = philHero?.image_url || "";

  const ctaTitle = ctaSection?.[`title_${lang}`] || ctaSection?.title_en || t("tabifoto.philosophy.cta_title");
  const ctaBody = ctaSection?.[`content_${lang}`] || ctaSection?.content_en || t("tabifoto.philosophy.cta_body");

  const NUM_LABELS = ["01", "02", "03", "04", "05", "06"];

  return (
    <div className="bg-[#F7F6F3]">
      <Navbar />

      {/* Hero — data-driven from homepage_sections (philosophy, sort_order = 0) */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <img
          src={heroImage}
          alt="TaBiFoTo philosophy"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2C2C2C]/80 via-[#2C2C2C]/30 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full pb-20 pt-32">
          <p className="text-xs uppercase tracking-[0.5em] text-[#C8B8A6] mb-6">Philosophy</p>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extralight text-white mb-6 leading-tight" style={{ letterSpacing: "-0.02em", maxWidth: "700px" }}>
            {heroTitle}
          </h1>
          <p className="text-base text-white/50 font-light max-w-lg leading-relaxed">{heroSub}</p>
        </div>
      </section>

      {/* Manifesto sections — data-driven from homepage_sections (philosophy, sort_order > 0) */}
      <section className="bg-[#F7F6F3] py-20 lg:py-28 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {philSections.map((section, idx) => (
              <article key={section.id} className="bg-white border border-[#D9D9D9]/60 rounded-lg p-8 lg:p-10">
                <span className="text-6xl font-extralight text-[#D9D9D9] leading-none block mb-6">
                  {NUM_LABELS[idx] ?? String(idx + 1).padStart(2, "0")}
                </span>
                <h4 className="text-xl lg:text-2xl font-light text-[#2C2C2C] mb-4" style={{ letterSpacing: "-0.01em" }}>
                  <a href={`#section-${NUM_LABELS[idx]}`} className="cursor-pointer hover:text-[#C8B8A6] transition-colors">
                    {section[`title_${lang}`] || section.title_en}
                  </a>
                </h4>
                <p className="text-sm text-[#2C2C2C]/50 font-light leading-relaxed">
                  {section[`content_${lang}`] || section.content_en}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Values — brand constants, kept in i18n */}
      <section className="bg-[#2C2C2C] py-20 lg:py-28 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.4em] text-[#C8B8A6] mb-4">Values</p>
            <h2 className="text-3xl lg:text-4xl font-extralight text-white" style={{ letterSpacing: "-0.02em" }}>
              {t("tabifoto.philosophy.values_title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {VALUES.map((val) => (
              <div key={val.titleKey} className="text-center bg-white/5 border border-white/10 rounded-lg p-8 hover:bg-white/10 transition-colors duration-300">
                <div className="w-12 h-12 flex items-center justify-center rounded-full border border-[#C8B8A6]/30 text-[#C8B8A6] mx-auto mb-5">
                  <i className={`${val.icon} text-xl`} />
                </div>
                <h3 className="text-lg font-light text-white mb-3">{t(val.titleKey)}</h3>
                <p className="text-sm text-white/40 font-light leading-relaxed">{t(val.bodyKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visual break */}
      <section className="relative h-80 overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/b22cadab1ff80cd329f010a017db9175.jpg"
          alt="Japan landscape"
          className="w-full h-full object-cover object-top opacity-60"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#F7F6F3]/60 to-transparent" />
        <div className="absolute inset-0 flex items-center px-6 lg:px-10 max-w-7xl mx-auto">
          <blockquote className="text-3xl lg:text-4xl font-extralight text-[#2C2C2C] max-w-lg leading-relaxed" style={{ letterSpacing: "-0.02em" }}>
            &ldquo;{t("tabifoto.storytelling.quote")}&rdquo;
          </blockquote>
        </div>
      </section>

      {/* CTA — data-driven from homepage_sections (cta) */}
      <section className="bg-[#F7F6F3] py-20 lg:py-24 px-6 text-center">
        <div className="max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-[0.4em] text-[#C8B8A6] mb-6">Ready?</p>
          <h2 className="text-3xl lg:text-4xl font-extralight text-[#2C2C2C] mb-4" style={{ letterSpacing: "-0.02em" }}>
            {ctaTitle}
          </h2>
          <p className="text-sm text-[#2C2C2C]/40 font-light mb-10 leading-relaxed">{ctaBody}</p>
          <Link to="/booking"
            className="inline-flex items-center gap-3 px-10 py-4 rounded-full bg-[#2C2C2C] text-white text-sm tracking-widest uppercase hover:bg-[#2C2C2C]/80 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            {t("tabifoto.philosophy.cta_button")}
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line" /></div>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
